import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Target, Sparkles, Send, CheckCircle2, TrendingUp, ChevronRight } from "lucide-react";

/**
 * SourcingRoiCard — surfaces the "hires from Talent Pool sourcing" funnel so
 * employers can prove ATS-driven sourcing beats external agencies.
 *
 * Data source: GET /api/skill-center/sourcing-roi?days=90
 *
 * Visible only for employer/recruiter roles. Silently hides itself when there
 * is nothing to show (invites_sent === 0) so first-time accounts don't stare
 * at a wall of zeros.
 */
export default function SourcingRoiCard({ days = 90 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/skill-center/sourcing-roi?days=${days}`)
            .then((r) => { setData(r.data); setError(false); })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [days]);

    if (loading) {
        return (
            <div className="border border-border bg-white p-4" data-testid="sourcing-roi-loading">
                <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Loading sourcing ROI…</div>
            </div>
        );
    }
    if (error || !data) return null;
    // Zero-state: hide the card entirely so first-time employers don't see empty numbers.
    // R22 — but keep it visible if the company has leak-digest clicks (even without invites).
    const hasLeakActivity = (data.leak_impact?.items || []).length > 0;
    if ((data.summary?.invites_sent || 0) === 0 && !hasLeakActivity) return null;

    const s = data.summary || {};
    const perRec = data.per_recruiter || [];
    const hires = data.recent_hires || [];

    return (
        <div className="border border-border bg-white" data-testid="sourcing-roi-card">
            <div className="px-4 py-2.5 border-b border-border bg-secondary/30 flex items-center gap-2 flex-wrap">
                <Target className="w-3.5 h-3.5 text-ikb" />
                <span className="text-[11px] font-mono uppercase tracking-widest text-ikb">
                    Talent Pool sourcing · last {data.window_days} days
                </span>
                <span className="text-[10px] text-muted-foreground ml-2">
                    Hires that came from Sourcing Suggestion invites — not organic applicants.
                </span>
                <Link to="/reports" className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline ml-auto" data-testid="sourcing-roi-open-reports">
                    Reports →
                </Link>
            </div>

            {/* Funnel row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border">
                <RoiStat icon={Send} label="Invites sent" value={s.invites_sent} testId="roi-invites-sent" />
                <RoiStat icon={CheckCircle2} label="Delivered" value={s.invites_delivered} hint={`${((s.invites_delivered / (s.invites_sent || 1)) * 100).toFixed(0)}%`} testId="roi-invites-delivered" />
                <RoiStat icon={TrendingUp} label="Accepted" value={s.invites_accepted} hint={`${s.acceptance_rate}%`} accent testId="roi-invites-accepted" />
                <RoiStat icon={Sparkles} label="Hired" value={s.invites_hired} hint={`${s.hire_rate}%`} strong testId="roi-invites-hired" />
            </div>

            {/* Per-recruiter leaderboard */}
            {perRec.length > 0 && (
                <div className="border-t border-border" data-testid="roi-per-recruiter">
                    <div className="px-4 py-2 flex items-center gap-2 border-b border-border">
                        <span className="text-[11px] font-mono uppercase tracking-widest">Top sourcing recruiters</span>
                        {perRec.length > 3 && (
                            <button
                                type="button"
                                onClick={() => setShowAll((v) => !v)}
                                className="text-[10px] font-mono text-ikb hover:underline ml-auto"
                                data-testid="roi-toggle-recruiters"
                            >
                                {showAll ? "Show top 3" : `Show all ${perRec.length}`}
                            </button>
                        )}
                    </div>
                    <div className="divide-y divide-border">
                        {(showAll ? perRec : perRec.slice(0, 3)).map((r, idx) => (
                            <div
                                key={r.recruiter_id}
                                className="px-4 py-2 flex items-center gap-3 hover:bg-secondary/20 text-sm"
                                data-testid={`roi-recruiter-${r.recruiter_id}`}
                            >
                                <span className="text-[10px] font-mono text-muted-foreground w-4 shrink-0">#{idx + 1}</span>
                                <span className="font-medium truncate flex-1 min-w-0">{r.recruiter_name}</span>
                                <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                                    {r.invites_sent} sent · {r.invites_accepted} accepted
                                </span>
                                <span className="text-[11px] font-mono border border-emerald-500 bg-emerald-50 text-emerald-800 px-1.5 shrink-0" title={`${r.hire_rate}% hire rate`}>
                                    {r.invites_hired} hired
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Recent hires */}
            {hires.length > 0 && (
                <div className="border-t border-border" data-testid="roi-recent-hires">
                    <div className="px-4 py-2 border-b border-border">
                        <span className="text-[11px] font-mono uppercase tracking-widest">Recent hires from sourcing</span>
                    </div>
                    <div className="divide-y divide-border">
                        {hires.slice(0, 5).map((h) => (
                            <div key={`${h.job_id}-${h.candidate_id}`} className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-secondary/20"
                                 data-testid={`roi-hire-${h.candidate_id}`}>
                                <Link to={`/candidates?open=${h.candidate_id}`} className="font-medium text-ikb hover:underline truncate max-w-[220px]" title={h.candidate_name}>
                                    {h.candidate_name || "Candidate"}
                                </Link>
                                <span className="text-[11px] text-muted-foreground truncate flex-1 min-w-0">
                                    {h.job_title ? `→ ${h.job_title}` : ""}
                                </span>
                                {h.skill && (
                                    <span className="text-[10px] font-mono border border-border bg-secondary/40 px-1 shrink-0">{h.skill}</span>
                                )}
                                <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                                    via {h.recruiter_name}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* R22 — Leak digest impact: click → hire correlation for the
                "Fix this leak" emails. Only rendered when there ARE clicks. */}
            {(data.leak_impact?.items || []).length > 0 && (
                <div className="border-t border-border" data-testid="roi-leak-impact">
                    <div className="px-4 py-2 border-b border-border flex items-center gap-2">
                        <span className="text-[11px] font-mono uppercase tracking-widest">Weekly-digest leak impact</span>
                        <span className="text-[10px] text-muted-foreground">clicks → hires since first click · click a row to see per-recruiter</span>
                    </div>
                    <div className="divide-y divide-border">
                        {(data.leak_impact.items || []).slice(0, 5).map((row) => (
                            <LeakImpactRow key={row.leak} row={row} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


function RoiStat({ icon: Icon, label, value, hint, accent = false, strong = false, testId }) {
    return (
        <div className="bg-white p-4" data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <Icon className={`w-3 h-3 ${strong ? "text-emerald-600" : accent ? "text-ikb" : ""}`} />
                <span>{label}</span>
            </div>
            <div className={`mt-1 font-display font-black text-2xl leading-tight ${strong ? "text-emerald-700" : accent ? "text-ikb" : ""}`}>
                {value ?? 0}
            </div>
            {hint && <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{hint}</div>}
        </div>
    );
}


/**
 * LeakImpactRow — a single leak line inside the "Weekly-digest leak impact"
 * section. Click the row to expand a per-user breakdown showing which
 * recruiters clicked the leak and any hires attributed to their first click.
 * Only expandable when the backend returns a ``per_user`` array.
 */
function LeakImpactRow({ row }) {
    const [expanded, setExpanded] = useState(false);
    const perUser = row.per_user || [];
    const label = row.leak.replace(/_to_/g, " → ").replace(/_/g, " ");
    const canExpand = perUser.length > 0;
    return (
        <div data-testid={`roi-leak-${row.leak}`}>
            <div className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-secondary/20">
                {canExpand ? (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-muted-foreground hover:text-ikb shrink-0"
                        aria-label={expanded ? "Collapse" : "Expand"}
                        data-testid={`roi-leak-toggle-${row.leak}`}
                    >
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
                    </button>
                ) : (
                    <span className="w-3.5 shrink-0" />
                )}
                <Link
                    to={`/pipeline?leak=${row.leak}`}
                    className="text-ikb hover:underline font-medium capitalize"
                    title={`Open pipeline focused on ${label}`}
                >
                    {label}
                </Link>
                <span className="text-[11px] font-mono text-muted-foreground ml-auto shrink-0">
                    {row.clicks} click{row.clicks === 1 ? "" : "s"} · {row.unique_users} recruiter{row.unique_users === 1 ? "" : "s"}
                </span>
                <span
                    className="text-[11px] font-mono border border-emerald-500 bg-emerald-50 text-emerald-800 px-1.5 shrink-0"
                    title="Hires whose stage updated after the earliest click on this leak"
                >
                    {row.hires_after_first_click} hired
                </span>
            </div>
            {expanded && perUser.length > 0 && (
                <div className="bg-secondary/20 border-t border-border" data-testid={`roi-leak-per-user-${row.leak}`}>
                    <div className="px-6 py-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        Per-recruiter attribution
                    </div>
                    <div className="divide-y divide-border/50">
                        {perUser.map((u) => (
                            <div
                                key={u.user_id || u.user_name}
                                className="px-6 py-1.5 flex items-center gap-3 text-[12px] hover:bg-white/40"
                                data-testid={`roi-leak-user-${row.leak}-${u.user_id || "unknown"}`}
                            >
                                <span className="font-medium truncate max-w-[200px]">{u.user_name || "Recruiter"}</span>
                                <span className="text-[10px] font-mono text-muted-foreground ml-auto shrink-0">
                                    {u.clicks} click{u.clicks === 1 ? "" : "s"}
                                </span>
                                <span
                                    className="text-[10px] font-mono border border-emerald-500 bg-emerald-50 text-emerald-800 px-1.5 shrink-0"
                                    title="Company hires whose stage moved after this recruiter's first click"
                                >
                                    {u.hires_after_first_click || 0} hired since first click
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

